import React, { useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "../styles/ComplaintDrafts.css";
import "../styles/CourtDraftsDisplay.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const UnifiedDraftsDisplay = () => {
  const { state } = useLocation();
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create refs dynamically based on draft type
  const draftRefs = useRef({});
  // Refs pointing to the draft CONTENT only (excludes the editable note and the
  // action buttons) so PDF / Print / Copy capture just the document body.
  const contentRefs = useRef({});

  // Extract common data from state
  const {
    query,
    summary,
    country,
    state: userState,
    district,
    lawCategory,
    draftType,
    // Court specific data
    applicantName,
    advocateName,
    courtName,
    // Other form specific data
    entityName,
    includeSections,
  } = state || {};

  // Debug logging for received state
  console.log("Received location state:", state);
  console.log("Draft Type:", draftType);

  // Safety check and defaults
  const safeCountry = country || "India";
  const safeLawCategory = lawCategory || "Criminal Law";
  const resolvedDraftType = draftType || "police";

  // Draft type configurations
  const draftConfigs = {
    police: {
      title: "Police Station Complaint",
      endpoint: "/api/drafts/generate",
      sections: ["police", "department"],
      sectionTitles: {
        police: "Police Station Complaint",
        department: "Department Complaint"
      }
    },
    court: {
      title: "Court Documents",
      endpoint: "/api/drafts/generate-court",
      sections: ["plaint", "affidavit", "vakalatnama"],
      sectionTitles: {
        plaint: "Plaint / Complaint",
        affidavit: "Affidavit",
        vakalatnama: "Vakalatnama"
      }
    },
    "legal-notice": {
      title: "Legal Notice",
      endpoint: "/api/drafts/generate-legal-notice",
      sections: ["legalNotice"],
      sectionTitles: {
        legalNotice: "Legal Notice"
      }
    },
    will: {
      title: "Will Document",
      endpoint: "/api/drafts/generate-will",
      sections: ["will"],
      sectionTitles: {
        will: "Last Will and Testament"
      }
    },
    agreement: {
      title: "Agreement/Contract",
      endpoint: "/api/drafts/generate-agreement",
      sections: ["agreement"],
      sectionTitles: {
        agreement: "Agreement/Contract"
      }
    },
    affidavit: {
      title: "Affidavit",
      endpoint: "/api/drafts/generate-affidavit",
      sections: ["affidavit"],
      sectionTitles: {
        affidavit: "Affidavit"
      }
    },
    application: {
      title: "General Application/Petition",
      endpoint: "/api/drafts/generate-application",
      sections: ["application"],
      sectionTitles: {
        application: "General Application/Petition"
      }
    },
    bail: {
      title: "Bail Application",
      endpoint: "/api/drafts/generate-bail",
      sections: ["bailApplication"],
      sectionTitles: {
        bailApplication: "Bail Application"
      }
    },
    consumer: {
      title: "Consumer Complaint",
      endpoint: "/api/drafts/generate-consumer",
      sections: ["consumerComplaint"],
      sectionTitles: {
        consumerComplaint: "Consumer Complaint"
      }
    },
    property: {
      title: "Property Documents",
      endpoint: "/api/drafts/generate-property",
      sections: ["propertyDocument"],
      sectionTitles: {
        propertyDocument: "Property Document"
      }
    },
    department: {
      title: "Department Complaint",
      endpoint: "/api/drafts/generate",
      sections: ["police", "department"],
      sectionTitles: {
        police: "Police Station Complaint",
        department: "Department Complaint"
      }
    },
    "police-only": {
      title: "Police Station Complaint",
      endpoint: "/api/drafts/generate",
      sections: ["police"],
      sectionTitles: {
        police: "Police Station Complaint"
      }
    },
    "department-only": {
      title: "Department Complaint",
      endpoint: "/api/drafts/generate",
      sections: ["department"],
      sectionTitles: {
        department: "Department Complaint"
      }
    }
  };

  useEffect(() => {
    if (!query && !summary) {
      setLoading(false);
      setError("No input provided.");
      return;
    }

    const fetchDrafts = async () => {
      setLoading(true);
      setError(null);

      try {
        const config = draftConfigs[resolvedDraftType] || draftConfigs.police;
        const requestData = {
          query: query || summary,
          summary: summary || query,
          country: safeCountry,
          state: userState,
          district,
          lawCategory: safeLawCategory,
          draftType: resolvedDraftType,
        };

        // Add specific data based on draft type
        if (resolvedDraftType === "court") {
          requestData.applicantName = applicantName;
          requestData.advocateName = advocateName;
          requestData.courtName = courtName;
        }

        console.log("Fetching drafts for type:", resolvedDraftType);
        console.log("Using endpoint:", config.endpoint);
        console.log("Request data:", requestData);

        const response = await axios.post(config.endpoint, requestData);

        if (resolvedDraftType === "court") {
          // Handle court-specific response structure
          setDrafts({
            plaint: response.data.plaint || "",
            affidavit: response.data.affidavit || "",
            vakalatnama: response.data.vakalatnama || "",
          });
        } else if (resolvedDraftType === "police-only" || resolvedDraftType === "department-only") {
          // Single-draft mode: backend generates only the requested draft.
          const draftsText = response.data.drafts || "";
          const cleaned = draftsText
            .replace("===POLICE_DRAFT===", "")
            .replace("===DEPARTMENT_DRAFT===", "")
            .trim();
          const sectionKey = resolvedDraftType === "police-only" ? "police" : "department";
          setDrafts({
            [sectionKey]: cleaned || "Draft could not be generated. Please try again.",
          });
        } else if (resolvedDraftType === "police" || resolvedDraftType === "department") {
          // Handle police/department response structure.
          // The backend prompt asks the model to separate the two drafts with
          // explicit markers so parsing is reliable across models.
          const draftsText = response.data.drafts || "";
          const policeMarker = "===POLICE_DRAFT===";
          const deptMarker = "===DEPARTMENT_DRAFT===";
          const pIdx = draftsText.indexOf(policeMarker);
          const dIdx = draftsText.indexOf(deptMarker);

          if (pIdx !== -1 && dIdx !== -1 && dIdx > pIdx) {
            const police = draftsText
              .substring(pIdx + policeMarker.length, dIdx)
              .trim();
            const department = draftsText
              .substring(dIdx + deptMarker.length)
              .trim();
            setDrafts({ police, department });
          } else {
            // Fallback for older heading style, else show the whole text so the
            // user still sees the generated draft instead of an error.
            const legacyPolice = draftsText.indexOf("Police Station Complaint Draft");
            const legacyDept = draftsText.indexOf("Court/Department Complaint Draft");
            if (legacyPolice !== -1 && legacyDept !== -1 && legacyDept > legacyPolice) {
              setDrafts({
                police: draftsText
                  .substring(legacyPolice, legacyDept)
                  .replace(/Police Station Complaint Draft/gi, "")
                  .trim(),
                department: draftsText
                  .substring(legacyDept)
                  .replace(/Court\/Department Complaint Draft/gi, "")
                  .trim(),
              });
            } else {
              setDrafts({
                police: draftsText.trim() || "Draft could not be generated. Please try again.",
                department: "",
              });
            }
          }
        } else {
          // Handle single draft responses (will, legal notice, etc.)
          const sectionKey = config.sections[0] || "draft";
          setDrafts({
            [sectionKey]: response.data.draft || response.data.drafts || "Draft generated successfully.",
          });
        }
      } catch (err) {
        console.error("Error generating drafts:", err);
        setError("Failed to generate drafts. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchDrafts();
  }, [query, summary, safeCountry, userState, district, safeLawCategory, resolvedDraftType, applicantName, advocateName, courtName]);

  // Create refs for each draft section
  useEffect(() => {
    const config = draftConfigs[resolvedDraftType] || draftConfigs.police;
    const sectionsToRender = Array.isArray(includeSections) && includeSections.length > 0
      ? includeSections
      : config.sections;

    sectionsToRender.forEach(section => {
      if (!draftRefs.current[section]) {
        draftRefs.current[section] = React.createRef();
      }
      if (!contentRefs.current[section]) {
        contentRefs.current[section] = React.createRef();
      }
    });
  }, [resolvedDraftType, includeSections]);

  const formatEditableDrafts = (text, section) => {
    if (!text) return null;
    return text
      .replace(/\*\*/g, "")
      .split(/\n{2,}/)
      .map((para, idx) => {
        const trimmed = para.trim();

        if (/^#{1,6}\s/.test(trimmed)) {
          const level = trimmed.match(/^#+/)[0].length;
          const content = trimmed.replace(/^#+\s*/, "");
          const safeLevel = Math.min(level, 4);
          const Tag = `h${safeLevel}`;
          return (
            <Tag
              key={`${section}-heading-${idx}`}
              contentEditable
              suppressContentEditableWarning
              className="editable-heading"
            >
              {content}
            </Tag>
          );
        }

        const lines = trimmed.split("\n");
        return (
          <p
            key={`${section}-para-${idx}`}
            contentEditable
            suppressContentEditableWarning
            className="editable-paragraph"
          >
            {lines.map((line, lineIdx) => (
              <React.Fragment key={lineIdx}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>
        );
      });
  };

  // Convert characters that jsPDF's default (Latin-1) font cannot render.
  const sanitizeForPdf = (text) =>
    String(text || "")
      .replace(/\u20B9/g, "Rs.")
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014]/g, "-")
      .replace(/\u2026/g, "...")
      .replace(/[\u2044\u2215]/g, "/")
      .replace(/\u00A0/g, " ")
      .replace(/[^\x00-\xFF]/g, "");

  const isDividerLine = (t) => /^([-*_=])\1{2,}$/.test(t.replace(/\s/g, ""));
  const isTableSeparator = (t) => /^\|?[\s:|-]+\|?$/.test(t) && t.includes("-");
  const splitTableRow = (t) =>
    t
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  // Build a text-based, multi-page PDF (selectable text, real tables) from the
  // edited DOM content so user edits and headings are preserved.
  const generatePDF = (contentRef, fileName, headingTitle) => {
    const el = contentRef.current;
    if (!el) return;

    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageW - margin * 2;
    let y = margin;

    const ensureSpace = (h) => {
      if (y + h > pageH - margin) {
        doc.addPage();
        y = margin;
      }
    };

    // Document title (centered).
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.splitTextToSize(sanitizeForPdf(headingTitle), maxWidth).forEach((tl) => {
      ensureSpace(9);
      doc.text(tl, pageW / 2, y, { align: "center" });
      y += 8;
    });
    y += 4;

    const renderParagraphText = (text) => {
      const lines = text.split(/\r\n|\r|\n/);
      let k = 0;
      while (k < lines.length) {
        const t = (lines[k] || "").trim();
        if (!t) {
          y += 3;
          k++;
          continue;
        }

        if (isDividerLine(t)) {
          ensureSpace(6);
          doc.setDrawColor(210);
          doc.line(margin, y, pageW - margin, y);
          y += 6;
          k++;
          continue;
        }

        // Markdown table: gather consecutive pipe rows.
        if (t.startsWith("|") && t.indexOf("|", 1) !== -1) {
          const tableLines = [];
          while (k < lines.length && lines[k].trim().startsWith("|")) {
            tableLines.push(lines[k].trim());
            k++;
          }
          const rows = tableLines
            .filter((l) => !isTableSeparator(l))
            .map((l) => splitTableRow(l).map((c) => sanitizeForPdf(c)));
          if (rows.length) {
            autoTable(doc, {
              startY: y,
              head: [rows[0]],
              body: rows.slice(1),
              margin: { left: margin, right: margin },
              styles: {
                font: "helvetica",
                fontSize: 9,
                cellPadding: 2,
                overflow: "linebreak",
                textColor: [40, 40, 40],
              },
              headStyles: {
                fillColor: [234, 244, 244],
                textColor: [25, 118, 210],
                fontStyle: "bold",
              },
            });
            y = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 4;
          }
          continue;
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.splitTextToSize(sanitizeForPdf(t), maxWidth).forEach((wl) => {
          ensureSpace(6);
          doc.text(wl, margin, y);
          y += 5.5;
        });
        y += 1.5;
        k++;
      }
      y += 2;
    };

    el.childNodes.forEach((node) => {
      if (node.nodeType !== 1) return; // elements only
      const txt = (node.innerText || "").trim();
      if (!txt) return;
      if (/^H[1-6]$/.test(node.tagName)) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.splitTextToSize(sanitizeForPdf(txt), maxWidth).forEach((l) => {
          ensureSpace(8);
          doc.text(l, margin, y);
          y += 7;
        });
        y += 1;
      } else {
        renderParagraphText(txt);
      }
    });

    doc.save(fileName);
  };

  const handleCopyText = (ref) => {
    if (!ref.current) return;
    const text = ref.current.innerText;
    navigator.clipboard.writeText(text).then(() => {
      alert("Text copied to clipboard!");
    });
  };

  // Turn the edited DOM content into clean, print-friendly HTML (real tables,
  // dividers) so the printout has proper paragraphs and paginates naturally.
  const buildPrintableHTML = (el) => {
    const escapeHtml = (s) =>
      String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const linesToHtml = (text) => {
      const lines = text.split(/\r\n|\r|\n/);
      let html = "";
      let k = 0;
      while (k < lines.length) {
        const t = (lines[k] || "").trim();
        if (!t) {
          k++;
          continue;
        }
        if (isDividerLine(t)) {
          html += "<hr/>";
          k++;
          continue;
        }
        if (t.startsWith("|") && t.indexOf("|", 1) !== -1) {
          const tableLines = [];
          while (k < lines.length && lines[k].trim().startsWith("|")) {
            tableLines.push(lines[k].trim());
            k++;
          }
          const rows = tableLines
            .filter((l) => !isTableSeparator(l))
            .map((l) => splitTableRow(l));
          if (rows.length) {
            html += "<table><thead><tr>";
            rows[0].forEach((c) => (html += `<th>${escapeHtml(c)}</th>`));
            html += "</tr></thead><tbody>";
            rows.slice(1).forEach((r) => {
              html += "<tr>";
              r.forEach((c) => (html += `<td>${escapeHtml(c)}</td>`));
              html += "</tr>";
            });
            html += "</tbody></table>";
          }
          continue;
        }
        const wrapped = [];
        while (k < lines.length && lines[k].trim() && !lines[k].trim().startsWith("|") && !isDividerLine(lines[k].trim())) {
          wrapped.push(escapeHtml(lines[k].trim()));
          k++;
        }
        html += `<p>${wrapped.join("<br/>")}</p>`;
      }
      return html;
    };

    let out = "";
    el.childNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      const txt = (node.innerText || "").trim();
      if (!txt) return;
      if (/^H[1-6]$/.test(node.tagName)) {
        out += `<h3>${escapeHtml(txt)}</h3>`;
      } else {
        out += linesToHtml(txt);
      }
    });
    return out;
  };

  const handlePrint = (contentRef, headingTitle) => {
    if (!contentRef.current) return;
    const bodyHtml = buildPrintableHTML(contentRef.current);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${headingTitle || "Draft"}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; color: #222; line-height: 1.5; padding: 24px 28px; }
            h2.doc-title { text-align: center; margin: 0 0 18px; }
            h3 { margin: 16px 0 6px; }
            p { margin: 0 0 10px; }
            hr { border: none; border-top: 1px solid #ccc; margin: 14px 0; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { border: 1px solid #bbb; padding: 6px 8px; text-align: left; font-size: 13px; vertical-align: top; }
            th { background: #eaf4f4; color: #1976d2; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h2 class="doc-title">${headingTitle || "Draft"}</h2>
          ${bodyHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleShare = (title) => {
    const shareData = {
      title,
      text: `Here is the ${title}.`,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch((err) => console.error("Share failed:", err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Share link copied to clipboard!");
    }
  };

  // Get configuration for current draft type
  const config = draftConfigs[resolvedDraftType] || draftConfigs.police;
  const visibleSections = Array.isArray(includeSections) && includeSections.length > 0
    ? includeSections
    : config.sections;
  const hasDrafts = Object.values(drafts).some(draft => draft && draft.trim());

  return (
    <div className="drafts-page">
      <div className="drafts-container">
        <h1 className="drafts-title">{config.title}</h1>
        <h3 className="drafts-query">Query: {query || summary}</h3>
        <h4 className="drafts-lawcat">Law Category: {safeLawCategory}</h4>

        {loading ? (
          <div className="loading-message">
            <p>Generating {config.title.toLowerCase()}...</p>
            <p>This may take a few moments. Please wait.</p>
          </div>
        ) : error ? (
          <div className="error-message">
            {error}
            <div style={{ marginTop: '10px' }}>
              <button onClick={() => window.history.back()} className="download-btn">
                Go Back to Form
              </button>
            </div>
          </div>
        ) : !hasDrafts ? (
          <div className="error-message">
            No {config.title.toLowerCase()} available. Please fill the form and submit to generate the documents.
            <div style={{ marginTop: '10px' }}>
              <button onClick={() => window.history.back()} className="download-btn">
                Go Back to Form
              </button>
            </div>
          </div>
        ) : (
          <>
            {visibleSections.map(section => {
              const draft = drafts[section];
              const sectionTitle = config.sectionTitles[section] || section.toUpperCase();
              
              if (!draft || !draft.trim()) return null;

              return (
                <div key={section} className="draft-section" ref={draftRefs.current[section]}>
                  <h2 className="draft-section-title">{sectionTitle}</h2>
                  <p className="editable-note">✏️ This is an editable draft. Click on any text to modify names, dates, or places.</p>
                  <div className="draft-content" ref={contentRefs.current[section]}>
                    {formatEditableDrafts(draft, section)}
                  </div>
                  <div className="download-buttons">
                    <button 
                      onClick={() => generatePDF(contentRefs.current[section], `${section}.pdf`, sectionTitle)} 
                      className="download-btn"
                    >
                      Download PDF
                    </button>
                    <button 
                      onClick={() => handlePrint(contentRefs.current[section], sectionTitle)} 
                      className="download-btn"
                    >
                      Print
                    </button>
                    <button 
                      onClick={() => handleCopyText(contentRefs.current[section])} 
                      className="download-btn"
                    >
                      Copy Text
                    </button>
                    <button 
                      onClick={() => handleShare(sectionTitle)} 
                      className="download-btn"
                    >
                      Share
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default UnifiedDraftsDisplay;
